import { Spinner } from '@app/components/icons/spinner';
import { CheckIcon } from '@app/features/analytics/icons/check';
import { CaretDown } from '@app/routes/content/routes/library/components/icons';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { formatDateToReadable } from '@app/utilities/utils';
import { Combobox, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, {
  Fragment,
  memo,
  Ref,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';

import { SearchIcon } from '../../icons/search';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { selectAnalyticsRecordingInfo } from '../../selectors';

export const VideoFilter = () => {
  const [isOpen, setIsOpen] = useState(false);

  const recordingInfo = useAppSelector(selectAnalyticsRecordingInfo);
  const dispatch = useAppDispatch();
  const { state, dispatch: analyticsDispatch } = useAnalyticsContext();
  const { t } = useTranslation('translation');

  const {
    filters,
    isRecordingsLoaded: hasRecordingsInitialized,
    recordingsData,
  } = recordingInfo;
  const searchText = filters.searchText;
  const pageStatus = recordingsData.status;
  const hasNextPage = recordingsData?.pagination?.next_cursor;
  const recordings = recordingsData?.data;
  const selectedVideos = state.selectedVideos;

  const { ref: loadingMoreRef, inView: loadingMoreInView } = useInView({
    rootMargin: '0px 0px 100px 0px',
    threshold: 0.1,
  });
  const { ref: noVideoRef, inView: noVideoInView } = useInView({
    rootMargin: '0px 0px 100px 0px',
    threshold: 0.1,
  });

  //fetch next recordings if the next page is available
  useEffect(() => {
    if (pageStatus === 'processing') return;
    if (loadingMoreInView && hasNextPage) {
      dispatch({ type: '[analytics] recording next page fetch' });
    }
  }, [
    loadingMoreInView,
    dispatch,
    hasNextPage,
    pageStatus,
    hasRecordingsInitialized,
  ]);

  //reset state on selector close when no video found for searched text
  useEffect(() => {
    return () => {
      if (
        searchText &&
        recordings.length === 0 &&
        pageStatus !== 'processing' &&
        pageStatus !== 'idle' &&
        noVideoInView
      ) {
        dispatch({ type: '[analytics] recording reset' });
      }
    };
  }, [noVideoInView, pageStatus, recordings.length, dispatch, searchText]);

  const handleSelect = useCallback(
    (videoTitles: string[]) => {
      analyticsDispatch({
        type: 'update selected videos',
        payload: { videos: videoTitles },
      });
    },
    [analyticsDispatch],
  );
  const handleUnselectAll = useCallback(
    e => {
      e.preventDefault();
      analyticsDispatch({
        type: 'update selected videos',
        payload: { videos: [] },
      });
    },
    [analyticsDispatch],
  );

  return (
    <div className="h-10">
      <Combobox
        value={selectedVideos}
        onChange={handleSelect}
        multiple
        as="div"
        className="relative"
      >
        {({ open }) => (
          <>
            <Combobox.Button
              className={cx(
                'relative flex cursor-pointer border border-neutral-b4 rounded-lg h-10 px-2 font-medium outline-none',
                {
                  'ring-2 ring-blue-500': open,
                },
              )}
              onClick={() => {
                if (
                  !open &&
                  !hasRecordingsInitialized &&
                  pageStatus != 'processing'
                ) {
                  dispatch({ type: '[analytics] recording fetch' });
                }
                setIsOpen(!isOpen);
              }}
            >
              <span
                className={cx('text-sm flex items-center gap-1', {
                  'text-blue-500': open,
                  'text-black': !open,
                })}
              >
                Videos
                {selectedVideos.length > 0 && (
                  <span className="text-xs flex items-center justify-center text-current bg-blue-500 text-white rounded-full px-2">
                    {selectedVideos.length}
                  </span>
                )}
              </span>
              <span className="pointer-events-none flex items-center pl-2">
                <CaretDown
                  width={16}
                  height={16}
                  className={`${open ? 'rotate-180' : ''}`}
                />
              </span>
            </Combobox.Button>

            {open && (
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute right-0 z-10 mt-1 w-80 overflow-hidden border border-neutral-b4 rounded-lg bg-white shadow-md outline-none">
                  <div>
                    <span className="absolute left-3 top-3">
                      <SearchIcon />
                    </span>
                    <Combobox.Input
                      value={searchText || ''}
                      placeholder={t('analytics.search-video-activity')}
                      onChange={evt => {
                        dispatch({
                          type: '[analytics] update recording search text',
                          payload: { searchText: evt.target.value },
                        });
                      }}
                      className="border leading-none text-sm py-1.5 pl-9 pr-1 h-9 m-1 w-[calc(100%-8px)] rounded-lg"
                    />
                  </div>
                  <MemoisedLoader
                    id="initializing-loader"
                    show={!hasRecordingsInitialized}
                    label={t('analytics.loading')}
                  />
                  <UnselectAllButton
                    hasSelectedVideos={!!selectedVideos.length}
                    hasRecordingsInitialized={hasRecordingsInitialized}
                    onClick={handleUnselectAll}
                    label={t('analytics.unselect-all')}
                  />
                  <Combobox.Options className="max-h-[300px] overflow-y-auto mt-1 mb-2">
                    {recordings.length === 0 && pageStatus !== 'processing' && (
                      <div
                        ref={noVideoRef}
                        className="h-5 w-full flex flex-row justify-center items-center"
                      >
                        {t('analytics.no-video')}
                      </div>
                    )}
                    {recordings.map(recording => {
                      const { id, title, status, thumbnail, started_at } =
                        recording;
                      const displayTitle =
                        title || formatDateToReadable(started_at);
                      if (status === 'processing') return null;
                      const isSelected = selectedVideos.includes(displayTitle);
                      return (
                        <Combobox.Option
                          key={id}
                          value={displayTitle}
                          className={cx(
                            'select-none px-3 py-2 flex items-center justify-between gap-1 cursor-pointer hover:bg-blue-100',
                            {
                              'bg-blue-100 text-blue-500': isSelected,
                              'bg-white text-black': !isSelected,
                            },
                          )}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {thumbnail && (
                              <img
                                src={thumbnail}
                                alt=""
                                className="w-8 h-8 object-cover rounded"
                              />
                            )}
                            <span className="truncate">{displayTitle}</span>
                          </div>
                          {isSelected && (
                            <span className="w-5 h-5 shrink-0">
                              <CheckIcon />
                            </span>
                          )}
                        </Combobox.Option>
                      );
                    })}
                    <MemoisedLoader
                      id={'more-loader'}
                      show={!!hasNextPage && pageStatus !== 'processing'}
                      _ref={loadingMoreRef}
                      label={t('analytics.loading')}
                    />
                  </Combobox.Options>
                </div>
              </Transition>
            )}
          </>
        )}
      </Combobox>
    </div>
  );
};

const UnselectAllButton = ({
  hasSelectedVideos,
  hasRecordingsInitialized,
  onClick,
  label,
}: {
  hasSelectedVideos: boolean;
  hasRecordingsInitialized: boolean;
  onClick: (e: any) => void;
  label: string;
}) => {
  if (!hasSelectedVideos || !hasRecordingsInitialized) return null;

  return (
    <div className="h-6 w-full flex flex-row justify-end pr-1">
      <Combobox.Button
        className="relative h-6 py-1 px-2 border text-xs border-neutral-b4 rounded-lg shadow-sm outline-none"
        onClick={onClick}
      >
        {label}
      </Combobox.Button>
    </div>
  );
};

const MemoisedLoader = memo(function Loader({
  id,
  show,
  _ref,
  label,
}: {
  id: string;
  show: boolean;
  _ref?: Ref<HTMLDivElement>;
  label: string;
}) {
  if (!show) return null;

  return (
    <div
      id={id}
      ref={_ref}
      className="h-5 w-full flex flex-row justify-center items-center"
    >
      <Spinner className="my-2" /> {label}
    </div>
  );
});
